import {FC, useState} from 'react';
import {Box, Divider, Tab, Tabs, TabsProps, Typography} from '@mui/material';
import {FormattedMessage, useIntl} from 'react-intl';
import {formatLocationTime} from 'src/utils/locationTime';
import {DetailedTransactionDTO, ClientType} from 'src/types/transactions';
import {convertMinutes} from 'src/utils/helper';
import {getEvDriverNameFromMinimal} from 'src/utils/userNameFormatter';
import {StatusTag} from '../ChargingSessionsTable/StatusTag';
import {TabPanel} from 'src/components/common/TabPanel';
import {ColorField} from 'src/components/common/ColorField';
import {CostTimeline} from './CostTimeline';
import {MeterValues} from './MeterValues';

interface ChargingSessionDetailsProps {
    transaction: DetailedTransactionDTO;
}

const tabsRootSx = {
    width: '100%',
};

const tabSx = {
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'rgba(0, 0, 0, 0.6)',
    // Its own width, not a share of the row: a scrollable strip only scrolls if the tabs in it keep
    // the width of their labels.
    minWidth: 'auto',
    flexShrink: 0,
    '&.Mui-selected': {
        color: 'primary.main',
        fontWeight: 600,
    },
};

const detailsWrapperSx = {
    backgroundColor: 'background.paper',
    borderRadius: 2,
    // 14px inside below a desktop rather than 24, and the same inset every card on a phone uses.
    // A tab's content sat in a frame the page had already stopped drawing around it.
    padding: {xs: '14px', md: 3},
    marginTop: 2,
};

const detailRowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    alignItems: 'center',
};

const labelSx = {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '0.875rem',
    fontWeight: 500,
};

const valueSx = {
    color: 'text.primary',
    fontSize: '0.875rem',
    fontWeight: 600,
};

const sectionTitleSx = {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: 2,
    marginTop: 2,
    color: 'text.primary',
};

export const ChargingSessionDetails: FC<ChargingSessionDetailsProps> = ({transaction}) => {
    const intl = useIntl();
    const [activeTab, setActiveTab] = useState<number>(0);
    // Every timestamp on this page is read against the charger, so the whole page runs on the
    // charger's clock rather than on the clock of whoever opened it.
    const locationTimezone = transaction.location?.timezone;

    const handleSetTab: TabsProps['onChange'] = (_event, newValue) => {
        setActiveTab(newValue);
    };

    const getCompanyName = (user: any): string => {
        if (user?.userType === 'PRIVATE_DRIVER') {
            return '-';
        }
        if (user?.userType === 'COMPANY' && user?.company?.name) {
            return user.company.name;
        }
        if (user?.userType === 'EMPLOYEE' && user?.employee?.company?.name) {
            return user.employee.company.name;
        }
        return '-';
    };

    const isActive = !transaction.stopTimestamp;

    return (
        <Box sx={tabsRootSx}>
            <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                {/* Scrollable, and not only on a phone: seven tabs do not fit a narrow window either.
                    Left to itself MUI squeezes them all into the width it has, and the last three -
                    RFID, meter values, cost - end up past the edge with no way to reach them. */}
                <Tabs
                    value={activeTab}
                    onChange={handleSetTab}
                    aria-label="session details tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                >
                    <Tab sx={tabSx} label={intl.formatMessage({id: 'generalInfo'})}/>
                    <Tab sx={tabSx} label={intl.formatMessage({id: 'locationInfo'})}/>
                    <Tab sx={tabSx} label={intl.formatMessage({id: 'chargerInfo'})}/>
                    <Tab sx={tabSx} label={intl.formatMessage({id: 'userInfo'})}/>
                    <Tab sx={tabSx} label={intl.formatMessage({id: 'rfid'})}/>
                    <Tab sx={tabSx} label={intl.formatMessage({id: 'meterValues'})}/>
                    <Tab sx={tabSx} label={intl.formatMessage({id: 'cost'})}/>
                </Tabs>
            </Box>

            {/* GENERAL INFO TAB */}
            <TabPanel value={activeTab} index={0}>
                <Box sx={detailsWrapperSx}>
                    <Typography sx={sectionTitleSx}>
                        <FormattedMessage id="sessionInformation"/>
                    </Typography>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>ID</Typography>
                        <Typography sx={valueSx}>{transaction.id}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="status"/>
                        </Typography>
                        <StatusTag isActive={isActive}/>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="clientType"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {transaction.clientType || ClientType.REGULAR}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="transactionStart"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {formatLocationTime(transaction.startTimestamp, locationTimezone)}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="transactionEnd"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {transaction.stopTimestamp
                                ? formatLocationTime(transaction.stopTimestamp, locationTimezone)
                                : '-'}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="chargingTime"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {convertMinutes(transaction.transactionSummary?.chargingTimeMinutes || 0)}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="connectedTime"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {convertMinutes(transaction.transactionSummary?.connectedTimeMinutes || 0)}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="powerConsumed"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {`${((transaction.transactionSummary?.wattsConsumed || 0) / 1000).toFixed(3)} ${intl.formatMessage({id: 'kWh'})}`}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="startValue"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {`${(transaction.startValue / 1000).toFixed(2)} ${intl.formatMessage({id: 'kWh'})}`}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="stopValue"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {transaction.stopValue
                                ? `${(transaction.stopValue / 1000).toFixed(2)} ${intl.formatMessage({id: 'kWh'})}`
                                : '-'}
                        </Typography>
                    </Box>

                    {transaction.stopReason && (
                        <>
                            <Divider/>
                            <Box sx={detailRowSx}>
                                <Typography sx={labelSx}>
                                    <FormattedMessage id="stopReason"/>
                                </Typography>
                                <Typography sx={valueSx}>{transaction.stopReason}</Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </TabPanel>

            {/* LOCATION INFO TAB */}
            <TabPanel value={activeTab} index={1}>
                <Box sx={detailsWrapperSx}>
                    <Typography sx={sectionTitleSx}>
                        <FormattedMessage id="locationInformation"/>
                    </Typography>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>ID</Typography>
                        <Typography sx={valueSx}>{transaction.location?.id || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="locationName"/>
                        </Typography>
                        <Typography sx={valueSx}>{transaction.location?.name || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="locationType"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {transaction.location?.locationType
                                ? intl.formatMessage({id: transaction.location.locationType})
                                : '-'}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="address"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {transaction.location?.address
                                ? [
                                    transaction.location.address.country,
                                    transaction.location.address.region,
                                    transaction.location.address.city,
                                    transaction.location.address.street,
                                    transaction.location.address.houseNumber,
                                    transaction.location.address.zipCode,
                                    transaction.location.address.parkingPlace,
                                ]
                                    .filter(Boolean)
                                    .join(', ')
                                : '-'}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="timezone"/>
                        </Typography>
                        <Typography sx={valueSx}>{transaction.location?.timezone || '-'}</Typography>
                    </Box>

                    {transaction.location?.realEstate && (
                        <>
                            <Divider/>
                            <Box sx={detailRowSx}>
                                <Typography sx={labelSx}>
                                    <FormattedMessage id="reOwner"/>
                                </Typography>
                                <Typography sx={valueSx}>{transaction.location.realEstate.name || '-'}</Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </TabPanel>

            {/* CHARGER INFO TAB */}
            <TabPanel value={activeTab} index={2}>
                <Box sx={detailsWrapperSx}>
                    <Typography sx={sectionTitleSx}>
                        <FormattedMessage id="chargerInformation"/>
                    </Typography>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>ID</Typography>
                        <Typography sx={valueSx}>{transaction.connector?.chargerId || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="chargePoint"/>
                        </Typography>
                        <Typography sx={valueSx}>{transaction.connector?.chargePointId || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="connectorId"/>
                        </Typography>
                        <Typography sx={valueSx}>{transaction.connector?.connectorId || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="connectorStatus"/>
                        </Typography>
                        <Typography sx={valueSx}>{transaction.connector?.currentStatus || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="stationModel"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {transaction.connector?.chargerVendor && transaction.connector?.chargerModelName
                                ? `${transaction.connector.chargerVendor} ${transaction.connector.chargerModelName}`
                                : '-'}
                        </Typography>
                    </Box>
                </Box>
            </TabPanel>

            {/* USER INFO TAB */}
            <TabPanel value={activeTab} index={3}>
                <Box sx={detailsWrapperSx}>
                    <Typography sx={sectionTitleSx}>
                        <FormattedMessage id="userInfo"/>
                    </Typography>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>ID</Typography>
                        <Typography sx={valueSx}>{transaction.user?.id || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="evDriverName"/>
                        </Typography>
                        <Typography sx={valueSx}>{getEvDriverNameFromMinimal(transaction.user)}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="userType"/>
                        </Typography>
                        {transaction.user?.userType ? (
                            <ColorField label={transaction.user.userType}/>
                        ) : (
                            <Typography sx={valueSx}>-</Typography>
                        )}
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="email"/>
                        </Typography>
                        <Typography sx={valueSx}>{transaction.user?.emailAddress || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="phoneNumber"/>
                        </Typography>
                        <Typography sx={valueSx}>{transaction.user?.phoneNumber || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="company"/>
                        </Typography>
                        <Typography sx={valueSx}>{getCompanyName(transaction.user)}</Typography>
                    </Box>

                    {transaction.user?.employee && (
                        <>
                            <Divider/>
                            <Box sx={detailRowSx}>
                                <Typography sx={labelSx}>
                                    <FormattedMessage id="employeeName"/>
                                </Typography>
                                <Typography sx={valueSx}>
                                    {`${transaction.user.employee.firstName || ''} ${transaction.user.employee.lastName || ''}`.trim() ||
                                        '-'}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </TabPanel>

            {/* RFID TAG TAB */}
            <TabPanel value={activeTab} index={4}>
                <Box sx={detailsWrapperSx}>
                    <Typography sx={sectionTitleSx}>
                        <FormattedMessage id="rfidTagInformation"/>
                    </Typography>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>ID</Typography>
                        <Typography sx={valueSx}>{transaction.idTag?.id || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="idTag"/>
                        </Typography>
                        <Typography sx={valueSx}>{transaction.idTag?.idTag || '-'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="licensePlate"/>
                        </Typography>
                        <Typography sx={valueSx}>{transaction.idTag?.licensePlate || '—'}</Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="blocked"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {transaction.idTag?.blocked
                                ? intl.formatMessage({id: 'yes'})
                                : intl.formatMessage({id: 'no'})}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="default"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {transaction.idTag?.isDefault
                                ? intl.formatMessage({id: 'yes'})
                                : intl.formatMessage({id: 'no'})}
                        </Typography>
                    </Box>
                    <Divider/>

                    <Box sx={detailRowSx}>
                        <Typography sx={labelSx}>
                            <FormattedMessage id="createdAt"/>
                        </Typography>
                        <Typography sx={valueSx}>
                            {transaction.idTag?.created
                                ? formatLocationTime(transaction.idTag.created, locationTimezone)
                                : '-'}
                        </Typography>
                    </Box>
                </Box>
            </TabPanel>

            {/* METER VALUES TAB */}
            <TabPanel value={activeTab} index={5}>
                <Box sx={detailsWrapperSx}>
                    <MeterValues
                        meterValues={transaction.connectorMeterValues || []}
                        wattsConsumed={transaction.transactionSummary?.wattsConsumed || 0}
                        connectedTimeMinutes={transaction.transactionSummary?.connectedTimeMinutes || 0}
                        locationTimezone={locationTimezone}
                    />
                </Box>
            </TabPanel>

            {/* COST TAB */}
            <TabPanel value={activeTab} index={6}>
                <Box sx={detailsWrapperSx}>
                    <CostTimeline
                        cost={transaction.cost}
                        wattsConsumed={transaction.transactionSummary?.wattsConsumed || 0}
                        transactionId={transaction.id}
                        locationTimezone={locationTimezone}
                    />
                </Box>
            </TabPanel>
        </Box>
    );
};